import axios from "axios";
import { AbortActionEnum } from "utils/enums/AbortActionEnum";
import { DataFormat } from "utils/format/DataFormat";
import {
  BaseApiResponseModel,
  Error,
} from "../../app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import ModelConverter from "../../utils/modelConverter";
import IApiClient from "./IApiClient";
import { productCache } from "utils/cache/productCache";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL!,
  timeout: 300000,
  paramsSerializer: {
    serialize: DataFormat.serializeParams,
  } as any,
});

api.interceptors.request.use(
  (config) => {
    const token = DataFormat.getToken();
    const request = config;

    if (token) {
      request.headers = {
        ...(request.headers || {}),
        Authorization: `Bearer ${token}`,
      } as any;
    }

    //for ngrok
    if (
      request?.url?.includes("ngrok-free.app") ||
      request?.url?.includes("ngrok-free.dev")
    ) {
      request.headers!["ngrok-skip-browser-warning"] = "69420";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    // Return JSON data
    if (response.data) {
      return response.data;
    }
    return response;
  },
  (error) => {
    if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }
    const err = {
      ...(error?.response || error),
      data: new Error().mapErrorRes({
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        ...(error?.response?.data?.error || error?.data),
      }),
    };
    if (error?.response?.status === 403) {
      localStorage.clear();
      sessionStorage.clear();
      productCache?.resetCache("");
      window.location.href = "/login";
    }
    return Promise.reject(err.data);
  }
);

class AxiosClient implements IApiClient {
  private abortControllers = new Map<string, AbortController>();

  private normalizeResponse<T extends Object>(
    response: any
  ): BaseApiResponseModel<T> {
    const payload = response?.data;
    const nestedData = payload?.data;
    const listData = nestedData?.items ?? payload?.items;
    const groupItems = nestedData?.groupItems ?? payload?.groupItems;
    const singleData = nestedData?.data ?? nestedData;
    const data = listData ?? groupItems ?? singleData ?? payload;
    const pagination =
      nestedData?.pagination ||
      nestedData?.paging ||
      payload?.pagination ||
      payload?.paging ||
      payload?.pagingnation ||
      response?.pagination ||
      response?.paging;
    const summary =
      nestedData?.summary || payload?.summary || response?.summary;
    const total = nestedData?.total || payload?.total || response?.total;

    return ModelConverter.decode(
      {
        ...response,
        rawData: payload,
        data,
        pagination,
        paging: pagination,
        pagingnation: payload?.pagingnation,
        summary,
        total,
        groupItems,
        isSaymee: payload?.isSaymee ?? response?.isSaymee,
        file: payload?.file || response?.file,
      },
      BaseApiResponseModel<T>
    );
  }

  private isCanceledRequest(error: any) {
    return axios.isCancel(error) || error?.code === "ERR_CANCELED";
  }

  private createAbort(path: string, type: AbortActionEnum) {
    const previousController = this.abortControllers.get(`${path}-${type}`);
    if (previousController) {
      previousController.abort();
    }

    const currentController = new AbortController();
    this.abortControllers.set(`${path}-${type}`, currentController);

    return currentController;
  }

  private clearAbort(
    path: string,
    type: AbortActionEnum,
    controller: AbortController
  ) {
    if (this.abortControllers.get(`${path}-${type}`) === controller) {
      this.abortControllers.delete(`${path}-${type}`);
    }
  }

  async post<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    const abortController = this.createAbort(path, AbortActionEnum.POST);
    try {
      let response = await api.post(path, data, {
        ...(config || {}),
        signal: abortController.signal,
      });
      return this.normalizeResponse<T>(response);
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined as BaseApiResponseModel<T>;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.POST, abortController);
    }
  }

  async get<T extends Object>(
    path: string,
    data: Map<string, any> | any = {}
  ): Promise<BaseApiResponseModel<T>> {
    const abortController = this.createAbort(path, AbortActionEnum.GET);
    try {
      let response = await api.get(path, {
        params: data,
        signal: abortController.signal,
      });
      return this.normalizeResponse<T>(response);
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined as BaseApiResponseModel<T>;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.GET, abortController);
    }
  }

  async getBlob(path: string, data: any = {}, config?: any): Promise<Blob> {
    const abortController = this.createAbort(path, AbortActionEnum.BLOB);
    try {
      const response = await api.get(path, {
        params: data,
        responseType: "blob",
        signal: abortController.signal,
        ...(config || {}),
      });
      return response as unknown as Blob;
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined as unknown as Blob;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.BLOB, abortController);
    }
  }

  async delete<T extends Object>(
    path: string,
    data: Map<string, any> | any = {}
  ): Promise<BaseApiResponseModel<T>> {
    const abortController = this.createAbort(path, AbortActionEnum.DELETE);
    try {
      let response = await api.delete(path, {
        params: data,
        signal: abortController.signal,
      });
      return this.normalizeResponse<T>(response);
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined as BaseApiResponseModel<T>;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.DELETE, abortController);
    }
  }

  async put<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    const abortController = this.createAbort(path, AbortActionEnum.PUT);
    try {
      let response = await api.put(path, data, {
        ...(config || {}),
        signal: abortController.signal,
      });
      return this.normalizeResponse<T>(response);
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined as BaseApiResponseModel<T>;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.PUT, abortController);
    }
  }

  async patch<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    const abortController = this.createAbort(path, AbortActionEnum.PATCH);
    try {
      let response = await api.patch(path, data, {
        ...(config || {}),
        signal: abortController.signal,
      });
      return this.normalizeResponse<T>(response);
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined as BaseApiResponseModel<T>;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.PATCH, abortController);
    }
  }

  async export(path: string, data: Map<string, any> | any = {}): Promise<any> {
    const abortController = this.createAbort(path, AbortActionEnum.EXPORT);
    try {
      const response = await api.get(path, {
        params: data,
        responseType: "arraybuffer",
        signal: abortController.signal,
      });
      return response;
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.EXPORT, abortController);
    }
  }

  async exportPost(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<any> {
    const abortController = this.createAbort(path, AbortActionEnum.EXPORT);
    try {
      const response = await api.post(path, data, {
        responseType: "arraybuffer",
        signal: abortController.signal,
        ...config,
      });
      return response;
    } catch (error) {
      if (this.isCanceledRequest(error)) {
        return undefined;
      }
      throw error;
    } finally {
      this.clearAbort(path, AbortActionEnum.EXPORT, abortController);
    }
  }
}

export default new AxiosClient();
